import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Landmark, Leaf, Award, ArrowRight } from 'lucide-react';
import Header from './components/Header6';
import Footer from './components/Footer6';

const About6 = () => {
    const navigate = useNavigate();

    const values = [
        { icon: Heart, title: 'Artisan First', desc: 'We work directly with over 200 weaver families, ensuring fair wages and dignified livelihoods. Every purchase you make directly supports an artisan household.' },
        { icon: Landmark, title: 'Heritage Craft', desc: 'We preserve weaving techniques that are centuries old. From Chanderi to Banarasi, Ikat to Ajrakh, we celebrate the rich textile traditions of India.' },
        { icon: Leaf, title: 'Earth Conscious', desc: 'All our textiles use natural fibres, vegetable dyes, and zero-waste weaving methods. We believe fashion should honour the earth it comes from.' },
        { icon: Award, title: 'Authentic Handloom', desc: 'Every piece carries a Handloom Mark or GI Tag certifying its authenticity. What you wear is genuinely hand-woven, never machine-made.' },
    ];

    const milestones = [
        { year: '1985', title: 'The First Thread', desc: 'Founded in a small workshop in Jaipur with three handlooms and a dream to bring authentic Indian textiles to discerning homes.' },
        { year: '1998', title: 'Artisan Network', desc: 'Expanded to work with 50 weaver families across Rajasthan, Madhya Pradesh, and Telangana, building a network rooted in trust.' },
        { year: '2008', title: 'National Recognition', desc: 'Received the National Handloom Award for excellence in preserving traditional weaving techniques and empowering artisan communities.' },
        { year: '2015', title: 'Digital Presence', desc: 'Launched our online platform to bring handloom textiles to customers across India and the world, without losing the personal touch.' },
        { year: '2020', title: 'Sustainability Pledge', desc: 'Committed to 100% natural dyes and zero-waste production. Launched our artisan welfare programme with health insurance and education support.' },
        { year: '2026', title: '200+ Artisan Families', desc: 'Today we support over 200 artisan families across 8 states, preserving 12 distinct weaving traditions for future generations.' },
    ];

    const stats = [
        { number: '200+', label: 'Artisan Families' },
        { number: '40+', label: 'Years of Heritage' },
        { number: '12', label: 'Weaving Traditions' },
        { number: '8', label: 'States Connected' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>
            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                <main>
                    {/* Hero */}
                    <section className="relative w-full min-h-[60vh] lg:min-h-[75vh] overflow-hidden">
                        <img src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Handloom heritage" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #3B1A08 0%, rgba(59,26,8,0.6) 50%, rgba(59,26,8,0.3) 100%)' }} />
                        <div className="relative z-10 h-full min-h-[60vh] lg:min-h-[75vh] flex flex-col justify-end pb-16 px-5 sm:px-8">
                            <div className="max-w-[1400px] mx-auto w-full">
                                <p className="text-xs font-semibold tracking-[3px] uppercase mb-4" style={{ color: '#D4C5B0' }}>Our Story</p>
                                <h1 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-4xl lg:text-6xl font-normal text-[#F5F0E8] tracking-wide mb-4">
                                    Rooted in the Earth,<br />Woven by Hand
                                </h1>
                                <p className="text-[#D4C5B0] text-base lg:text-lg max-w-xl" style={{ fontWeight: 300, lineHeight: 1.7 }}>
                                    For over four decades, we have been connecting master artisans with people who value authenticity, heritage, and the warmth of handmade textiles.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Values */}
                    <section className="py-16 lg:py-28 bg-[#FAF7F2]">
                        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                            <div className="text-center mb-12 lg:mb-16">
                                <p className="text-xs font-semibold tracking-[3px] uppercase mb-3" style={{ color: '#8B4513' }}>What We Believe</p>
                                <h2 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-3xl lg:text-4xl font-normal text-[#2C1810]">Our Values</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                {values.map((v, i) => (
                                    <div key={i} className="text-center">
                                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'rgba(139,69,19,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                            <v.icon size={24} style={{ color: '#8B4513' }} />
                                        </div>
                                        <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '18px', fontWeight: 600, color: '#2C1810', marginBottom: '8px' }}>{v.title}</h3>
                                        <p style={{ fontSize: '14px', color: '#7C6B5D', lineHeight: 1.7, fontWeight: 300 }}>{v.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Stats */}
                    <section className="py-16 lg:py-20" style={{ backgroundColor: '#3B1A08' }}>
                        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                                {stats.map((s, i) => (
                                    <div key={i} className="text-center">
                                        <p style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '36px', fontWeight: 700, color: '#F5F0E8', marginBottom: '4px' }}>{s.number}</p>
                                        <p style={{ fontSize: '13px', color: '#D4C5B0', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 600 }}>{s.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Timeline */}
                    <section className="py-16 lg:py-28 bg-[#F5F0E8]">
                        <div className="max-w-[800px] mx-auto px-5 sm:px-8">
                            <div className="text-center mb-12 lg:mb-16">
                                <p className="text-xs font-semibold tracking-[3px] uppercase mb-3" style={{ color: '#8B4513' }}>Our Journey</p>
                                <h2 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-3xl lg:text-4xl font-normal text-[#2C1810]">Milestones</h2>
                            </div>
                            <div className="space-y-8">
                                {milestones.map((m, i) => (
                                    <div key={i} className="flex gap-6">
                                        <div className="flex flex-col items-center">
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#8B4513', flexShrink: 0 }} />
                                            {i < milestones.length - 1 && <div style={{ width: '1px', flex: 1, backgroundColor: '#D4C5B0', marginTop: '8px' }} />}
                                        </div>
                                        <div className="pb-2">
                                            <p style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '14px', fontWeight: 600, color: '#8B4513', marginBottom: '4px' }}>{m.year}</p>
                                            <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '18px', fontWeight: 600, color: '#2C1810', marginBottom: '8px' }}>{m.title}</h3>
                                            <p style={{ fontSize: '14px', color: '#7C6B5D', lineHeight: 1.7, fontWeight: 300 }}>{m.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="py-16 lg:py-24 bg-[#FAF7F2] text-center">
                        <div className="max-w-[600px] mx-auto px-5">
                            <h2 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-3xl lg:text-4xl font-normal text-[#2C1810] mb-4">Wear the Heritage</h2>
                            <p style={{ fontSize: '15px', color: '#7C6B5D', lineHeight: 1.7, marginBottom: '32px', fontWeight: 300 }}>
                                Every Dharti piece carries the story of an artisan, the warmth of natural fibres, and the soul of Indian heritage. Explore our collection today.
                            </p>
                            <button onClick={() => navigate('/template6/shop')} className="inline-flex items-center gap-2" style={{ padding: '16px 36px', backgroundColor: '#8B4513', color: '#FFFFFF', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
                                Explore Collection <ArrowRight size={16} />
                            </button>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default About6;
